import { useMemo } from 'react'
import { sortTasks } from '../utils/sortAndGroupTasks'
import { useProjectEmptyStates } from './ProjectEmptyStates'
import type { ProjectListViewProps } from '../types'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import { TaskPageLayout } from '@/routes/features/tasks/components/TaskPageLayout'
import { StatusGroupedTaskView } from '@/routes/features/tasks/components/StatusGroupedTaskView'
import { useProjectTasks } from '@/routes/features/tasks/hooks/useProjectTasks'
import useProjectFilterStore from '@/store/ProjectFilterStore'
import useProjectViewPreferencesStore from '@/store/ProjectViewPreferencesStore'
import { EMPTY_FILTERS, getActiveFilterCount } from '@/lib/types/filter'
import { FilterZeroResults } from '@/components/features/filters/FilterZeroResults'
import useViewModeStore from '@/store/ViewModeStore'

export function ProjectListView({ projectId }: ProjectListViewProps) {
  const filters = useProjectFilterStore(
    (s) => s.filters[projectId] ?? EMPTY_FILTERS,
  )
  const { tasks, topLevelTasks, unfilteredTopLevelTasks, filterContext, isLoading } =
    useProjectTasks(projectId, filters)
  // Display → Show sub-tasks flattens them into the list (design 28b).
  const showSubTasks = useViewModeStore((s) => s.showSubTasks)
  const listTasks = showSubTasks ? tasks : topLevelTasks
  const detailsPanel = useProjectViewPreferencesStore(
    (s) => s.preferences[projectId]?.detailsPanel ?? 'on',
  )
  const sortBy = useProjectViewPreferencesStore(
    (s) => s.preferences[projectId]?.sortBy ?? 'manual',
  )
  const sortDirection = useProjectViewPreferencesStore(
    (s) => s.preferences[projectId]?.sortDirection ?? 'asc',
  )
  const listGroupBy = useProjectViewPreferencesStore(
    (s) => s.preferences[projectId]?.listGroupBy ?? 'status',
  )

  const { emptyIntro, renderEmptyState } = useProjectEmptyStates(projectId)

  const transformItems = useMemo(
    () => (items: Array<TaskDocument>) => sortTasks(items, sortBy, sortDirection),
    [sortBy, sortDirection],
  )

  if (!isLoading && listTasks.length === 0 && getActiveFilterCount(filters) > 0) {
    return (
      <FilterZeroResults
        projectId={projectId}
        unfilteredTasks={unfilteredTopLevelTasks}
        filterContext={filterContext}
      />
    )
  }

  return (
    <TaskPageLayout
      tasks={listTasks}
      isLoading={isLoading}
      transformItems={transformItems}
      renderTaskView={listGroupBy === 'status' ? StatusGroupedTaskView : undefined}
      navigateToSubtasks={true}
      showViewModeToggle={false}
      showAddTaskInput={true}
      projectId={projectId}
      viewModeOverride={detailsPanel === 'on' ? 'split' : 'simple'}
      emptyIntro={emptyIntro}
      renderEmptyState={renderEmptyState}
    />
  )
}
